import React from 'react';
import {AvForm} from 'availity-reactstrap-validation';
import DataTable from '../../shared/component/dataTable/DataTable';
import PurchaseRequisitionColumn from '../../shared/component/dataTable/columns/PurchaseRequisitionColumn.json';
import {Button} from 'reactstrap';
import {toast} from 'react-toastify';
import GeneralUtils from '../../shared/utils/GeneralUtils';
import LicenseService from '../../shared/service/LicenseService';
import {PURCHASE_REQUISITION_APPROVAL_FILTER, PURCHASE_REQUISITION_STATUS} from '../../shared/utils/Constants';
import {resolvePurchaseRequisitionStatusFromQuery} from '../dasboard/dashboardMenuUtils';
import {queryTokenForStatus, rememberListReturnHash} from './PurchaseRequisitionListNav';
import PurchaseRequisitionNavShortcuts, {shortcutIdForRequisitionStatus} from './PurchaseRequisitionNavShortcuts';
import {getRequisitionDocLabels} from './PurchaseRequisitionUtils';
import '../../shared/styles/SettingsPage.css';

const LIST_FILTER_STORAGE_KEY = 'purchaseRequisition.listFilter';
const LICENSE_REQUIRED_MESSAGE = "You don't have an invoice license.";

/** Merged PR + PO page only (Delivery Note / Return keep their own menus). */
const MERGED_PAGE_STATUSES = PURCHASE_REQUISITION_STATUS.filter(item =>
    item.value === 'OPEN' || item.value === 'PURCHASE_ORDER'
);

function readStatusQueryFromHash() {
    const hash = window.location.hash || '';
    const queryPart = hash.includes('?') ? hash.substring(hash.indexOf('?') + 1) : '';
    return resolvePurchaseRequisitionStatusFromQuery(queryPart);
}

function pageTitleForStatus(requisitionStatus) {
    if (!requisitionStatus || requisitionStatus === 'OPEN') {
        return 'Requisition';
    }
    const match = PURCHASE_REQUISITION_STATUS.find(item => item.value === requisitionStatus);
    return match ? match.name : 'Purchase Order';
}

function loadStoredListFilter(lockedStatus) {
    if (lockedStatus) {
        const needsApproval = lockedStatus === 'DELIVERY_NOTE' || lockedStatus === 'RETURN';
        return {
            requisitionStatus: lockedStatus,
            approvedFilter: needsApproval ? 'false' : 'false',
            statusLocked: true
        };
    }
    try {
        const raw = sessionStorage.getItem(LIST_FILTER_STORAGE_KEY);
        if (!raw) {
            return {requisitionStatus: 'OPEN', approvedFilter: 'false', statusLocked: false};
        }
        const parsed = JSON.parse(raw);
        const mergedValues = MERGED_PAGE_STATUSES.map(item => item.value);
        // Unlocked list is PR+PO only — ignore DN/Return leftover from session.
        const requisitionStatus = mergedValues.includes(parsed?.requisitionStatus)
            ? parsed.requisitionStatus
            : 'OPEN';
        return {requisitionStatus, approvedFilter: 'false', statusLocked: false};
    } catch (e) {
        return {requisitionStatus: 'OPEN', approvedFilter: 'false', statusLocked: false};
    }
}

function saveStoredListFilter(requisitionStatus, approvedFilter, statusLocked) {
    if (statusLocked) {
        return;
    }
    try {
        sessionStorage.setItem(LIST_FILTER_STORAGE_KEY, JSON.stringify({
            requisitionStatus,
            approvedFilter
        }));
    } catch (e) {
        // ignore storage errors
    }
}

export default class PurchaseRequisition extends React.Component {
    constructor(props) {
        super(props);
        const lockedStatus = readStatusQueryFromHash();
        const stored = loadStoredListFilter(lockedStatus);
        this.state = {
            hasInvoiceLicense: false,
            requisitionStatus: stored.requisitionStatus,
            approvedFilter: stored.approvedFilter,
            statusLocked: stored.statusLocked
        };
        this.refDataTable = null;
        this.licenseService = new LicenseService();
    }

    componentDidMount() {
        if (GeneralUtils.isMobile()) {
            const lockedStatus = readStatusQueryFromHash();
            window.location.href = lockedStatus
                ? '#/inventoryManagementMobile?' + queryTokenForStatus(lockedStatus)
                : '#/inventoryManagementMobile';
            return;
        }
        if (this.state.statusLocked) {
            rememberListReturnHash(this.state.requisitionStatus, true);
        } else {
            rememberListReturnHash(this.state.requisitionStatus, false);
        }
        window.addEventListener('hashchange', this.syncStatusFromHash);
        this.checkInvoiceLicense();
    }

    checkInvoiceLicense = () => {
        this.licenseService.getLicenses(localStorage.getItem('merchantBranchId')).then(response => {
            if (!GeneralUtils.isNullOrEmpty(response) && GeneralUtils.isNullOrEmpty(response.type)
                && response.hasInvoiceLicense === true) {
                this.setState({hasInvoiceLicense: true}, () => {
                    if (this.refDataTable) {
                        this.refreshDataTable();
                    }
                });
            } else {
                toast.warn(LICENSE_REQUIRED_MESSAGE);
            }
        });
    };

    componentWillUnmount() {
        window.removeEventListener('hashchange', this.syncStatusFromHash);
    }

    syncStatusFromHash = () => {
        const lockedStatus = readStatusQueryFromHash();
        const next = loadStoredListFilter(lockedStatus);
        rememberListReturnHash(next.requisitionStatus, next.statusLocked);
        this.setState({
            requisitionStatus: next.requisitionStatus,
            approvedFilter: next.approvedFilter,
            statusLocked: next.statusLocked
        }, () => this.refreshDataTable());
    };

    setDataTableRef = (elem) => {
        this.refDataTable = elem;
        if (elem && this.state.hasInvoiceLicense) {
            this.refreshDataTable();
        }
    };

    getListFields = () => {
        const showApproval = this.state.requisitionStatus === 'DELIVERY_NOTE'
            || this.state.requisitionStatus === 'RETURN';
        const {docNoLabel} = getRequisitionDocLabels(this.state.requisitionStatus);
        // Status is already chosen via combo / locked page title — hide Status column.
        return PurchaseRequisitionColumn.fields.map(field => {
            if (field.field === 'requisitionNo') {
                return {...field, header: docNoLabel};
            }
            if (field.field === 'requisitionStatus') {
                return {...field, visible: false};
            }
            if (field.field === 'approvalDisplay') {
                return {...field, visible: showApproval};
            }
            return field;
        });
    };

    render() {
        if (GeneralUtils.isMobile()) {
            return null;
        }

        const showApprovalFilter = this.state.requisitionStatus === 'DELIVERY_NOTE'
            || this.state.requisitionStatus === 'RETURN';
        const showDocTypeSeg = !this.state.statusLocked;
        const showCreate = this.state.requisitionStatus === 'OPEN'
            || this.state.requisitionStatus === 'DELIVERY_NOTE'
            || this.state.requisitionStatus === 'RETURN';
        const showSeg = showDocTypeSeg || showApprovalFilter;
        const pageTitle = pageTitleForStatus(this.state.requisitionStatus);

        return (
            <div className="settings-page settings-page--full col-md-12">
                <AvForm>
                    <div className="settings-page__shell">
                        <div className="settings-page__header settings-page__header--with-shortcuts">
                            <h1 className="settings-page__title">{pageTitle}</h1>
                            <PurchaseRequisitionNavShortcuts
                                activeId={shortcutIdForRequisitionStatus(this.state.requisitionStatus)}
                            />
                        </div>
                        <div className="settings-page__panel">
                            <div className="settings-page__invoice-toolbar">
                                <div className="settings-page__invoice-toolbar-row">
                                    <div className="settings-page__invoice-toolbar-primary">
                                        {showDocTypeSeg &&
                                            <div
                                                className="settings-page__invoice-status-seg"
                                                role="group"
                                                aria-label="Document type"
                                            >
                                                {MERGED_PAGE_STATUSES.map(element => {
                                                    const active = this.state.requisitionStatus === element.value;
                                                    return (
                                                        <button
                                                            key={element.value}
                                                            type="button"
                                                            className={`settings-page__invoice-status-seg__btn${active ? ' is-active' : ''}`}
                                                            aria-pressed={active}
                                                            onClick={() => this.setRequisitionStatus(element.value)}
                                                        >
                                                            {element.name}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        }
                                        {showApprovalFilter &&
                                            <div
                                                className="settings-page__invoice-status-seg"
                                                role="group"
                                                aria-label="Approval status"
                                            >
                                                {PURCHASE_REQUISITION_APPROVAL_FILTER.map(element => {
                                                    const active = String(this.state.approvedFilter) === String(element.value);
                                                    return (
                                                        <button
                                                            key={element.value}
                                                            type="button"
                                                            className={`settings-page__invoice-status-seg__btn${active ? ' is-active' : ''}`}
                                                            aria-pressed={active}
                                                            onClick={() => this.setApprovedFilter(element.value)}
                                                        >
                                                            {String(element.name).toUpperCase()}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        }
                                        {showSeg && showCreate && this.state.hasInvoiceLicense &&
                                            <span className="settings-page__invoice-toolbar-divider" aria-hidden="true"/>
                                        }
                                        {showCreate && this.state.hasInvoiceLicense &&
                                            <div className="settings-page__invoice-toolbar-actions">
                                                {this.state.requisitionStatus === 'OPEN' &&
                                                    <Button
                                                        color="outline-primary"
                                                        className="settings-page__btn--add settings-page__btn--create-pr"
                                                        onClick={this.addNew}
                                                    >
                                                        <i className="pi pi-plus" aria-hidden="true"/>
                                                        Create Requisition
                                                    </Button>
                                                }
                                                {this.state.requisitionStatus === 'DELIVERY_NOTE' &&
                                                    <Button
                                                        color="outline-primary"
                                                        className="settings-page__btn--add settings-page__btn--create-dn"
                                                        onClick={this.addNewDeliveryNote}
                                                    >
                                                        <i className="pi pi-plus" aria-hidden="true"/>
                                                        Create Delivery Note
                                                    </Button>
                                                }
                                                {this.state.requisitionStatus === 'RETURN' &&
                                                    <Button
                                                        color="outline-primary"
                                                        className="settings-page__btn--add settings-page__btn--create-return"
                                                        onClick={this.addNewReturn}
                                                    >
                                                        <i className="pi pi-plus" aria-hidden="true"/>
                                                        Create Return Note
                                                    </Button>
                                                }
                                            </div>
                                        }
                                    </div>
                                </div>
                            </div>

                            {this.state.hasInvoiceLicense &&
                                <div className="settings-page__table-wrap">
                                    <DataTable
                                        ref={this.setDataTableRef}
                                        fields={this.getListFields()}
                                        objectName={PurchaseRequisitionColumn.name}
                                        dataURL="inventory/PurchaseRequisition/all"
                                        rows={10}
                                        autoLoad={false}
                                        selectionMode="single"
                                        onDataSelect={this.onDataSelect}
                                    />
                                </div>
                            }
                        </div>
                    </div>
                </AvForm>
            </div>
        );
    }

    refreshDataTable = () => {
        if (!this.state.hasInvoiceLicense) {
            return;
        }
        const table = this.refDataTable;
        if (!table || typeof table.findAll !== 'function') {
            return;
        }
        const {requisitionStatus, approvedFilter, statusLocked} = this.state;
        saveStoredListFilter(requisitionStatus, approvedFilter, statusLocked);
        const filter = [
            {
                name: 'requisitionStatus',
                value: requisitionStatus,
                type: 'string',
                filterMatchMode: 'equals'
            }
        ];
        const filterElement = {requisitionStatus};
        const useApprovalFilter = requisitionStatus === 'DELIVERY_NOTE'
            || requisitionStatus === 'RETURN';
        if (useApprovalFilter) {
            // softrame: boolean equals must use type=select + subType=boolean (avoids lower() on BOOL)
            filter.push({
                name: 'approved',
                value: approvedFilter === 'true' ? 'true' : 'false',
                type: 'select',
                subType: 'boolean',
                filterMatchMode: 'equals'
            });
            filterElement.approved = approvedFilter === 'true' ? 'true' : 'false';
        }

        table.setState({filter, filterElement, first: 0, loading: true});
        const gridModel = {
            filter,
            sort: table.state.sort || [],
            pageNumber: 0,
            limit: table.state.rows
        };
        table.findAll(gridModel).then(response => {
            if (!GeneralUtils.isNullOrEmpty(response) && GeneralUtils.isNullOrEmpty(response.type)) {
                table.setState({
                    data: response.data,
                    totalRecords: response.totalCount != null ? response.totalCount : 0,
                    loading: false
                });
            } else {
                table.setState({loading: false});
            }
        }).catch(() => table.setState({loading: false}));
    };

    onDataSelect = (data) => {
        if (!this.state.hasInvoiceLicense) {
            return toast.warn(LICENSE_REQUIRED_MESSAGE);
        }
        if (data?.id) {
            saveStoredListFilter(this.state.requisitionStatus, this.state.approvedFilter, this.state.statusLocked);
            rememberListReturnHash(this.state.requisitionStatus, this.state.statusLocked);
            window.location.href = '#/purchaseRequisitionSave?' + data.id;
        }
    };

    addNew = () => {
        if (!this.state.hasInvoiceLicense) {
            return toast.warn(LICENSE_REQUIRED_MESSAGE);
        }
        saveStoredListFilter(this.state.requisitionStatus, this.state.approvedFilter, this.state.statusLocked);
        rememberListReturnHash(this.state.requisitionStatus, this.state.statusLocked);
        window.location.href = '#/purchaseRequisitionSave?-1';
    };

    addNewDeliveryNote = () => {
        if (!this.state.hasInvoiceLicense) {
            return toast.warn(LICENSE_REQUIRED_MESSAGE);
        }
        saveStoredListFilter(this.state.requisitionStatus, this.state.approvedFilter, this.state.statusLocked);
        rememberListReturnHash(this.state.requisitionStatus, this.state.statusLocked);
        window.location.href = '#/purchaseRequisitionSave?-1&mode=delivery';
    };

    addNewReturn = () => {
        if (!this.state.hasInvoiceLicense) {
            return toast.warn(LICENSE_REQUIRED_MESSAGE);
        }
        saveStoredListFilter(this.state.requisitionStatus, this.state.approvedFilter, this.state.statusLocked);
        rememberListReturnHash(this.state.requisitionStatus, this.state.statusLocked);
        window.location.href = '#/purchaseRequisitionSave?-1&mode=return';
    };

    setRequisitionStatus = (value) => {
        if (this.state.statusLocked) {
            return;
        }
        const allowed = MERGED_PAGE_STATUSES.some(item => item.value === value);
        if (!allowed || value === this.state.requisitionStatus) {
            return;
        }
        this.setState({
            requisitionStatus: value,
            approvedFilter: 'false'
        }, () => this.refreshDataTable());
    };

    setApprovedFilter = (value) => {
        if (String(value) === String(this.state.approvedFilter)) {
            return;
        }
        this.setState({approvedFilter: value}, () => this.refreshDataTable());
    };

    __handleChange = (e) => {
        const value = e.target.parsedValue !== undefined ? e.target.parsedValue : e.target.value;
        this.setRequisitionStatus(value);
    };

    __handleApprovalFilterChange = (e) => {
        const value = e.target.parsedValue !== undefined ? e.target.parsedValue : e.target.value;
        this.setApprovedFilter(value);
    };
}
